import { lockPageScroll, unlockPageScroll } from '@theme/dialog'
import { trapFocus, removeTrapFocus } from '@theme/focus'
import { THEME_BREAKPOINTS } from '@theme/utilities'

/**
 * @param {string} value
 * @returns {number}
 */
export function parseTransitionTime(value) {
  const trimmedValue = value.trim()
  if (!trimmedValue) return 0
  if (trimmedValue.endsWith('ms')) {
    return Number.parseFloat(trimmedValue)
  }
  if (trimmedValue.endsWith('s')) {
    return Number.parseFloat(trimmedValue) * 1000
  }

  return Number.parseFloat(trimmedValue) || 0
}

/**
 * @param {HTMLElement} element
 * @param {string} propertyName
 * @returns {number}
 */
export function getTransitionTimeoutMs(element, propertyName) {
  const styles = window.getComputedStyle(element)
  const properties = styles.transitionProperty
    .split(',')
    .map((value) => value.trim())
  const durations = styles.transitionDuration
    .split(',')
    .map(parseTransitionTime)
  const delays = styles.transitionDelay.split(',').map(parseTransitionTime)

  let timeoutMs = 0
  for (let index = 0; index < properties.length; index += 1) {
    const property =
      properties[index] || properties[properties.length - 1] || 'all'
    if (property !== 'all' && property !== propertyName) continue

    const duration = durations[index] ?? durations[durations.length - 1] ?? 0
    const delay = delays[index] ?? delays[durations.length - 1] ?? 0
    timeoutMs = Math.max(timeoutMs, duration + delay)
  }

  return timeoutMs
}

/**
 * @param {ParentNode} root
 * @param {Element} control
 * @returns {HTMLElement | null}
 */
export function resolvePanelByControls(root, control) {
  if (!(root instanceof HTMLElement) || !(control instanceof Element))
    return null

  const panelId = control.getAttribute('aria-controls')
  if (!panelId) return null

  const panel = root.querySelector(`#${CSS.escape(panelId)}`)
  return panel instanceof HTMLElement ? panel : null
}

/**
 * @param {HTMLElement} toggle
 */
export function syncSupportItemExpandedState(toggle) {
  const item = toggle.closest('.header-mega-menu__support-item')
  if (!(item instanceof HTMLElement)) return
  const expanded = toggle.getAttribute('aria-expanded') === 'true'
  item.classList.toggle('header-mega-menu__support-item--expanded', expanded)
}

/**
 * @param {{ headerGroup?: HTMLElement | null, isOpen: boolean }} params
 */
export function setMegaMenuOpenState({ headerGroup, isOpen }) {
  headerGroup?.setAttribute('data-mega-menu-open', String(isOpen))
  document.body.setAttribute('data-header-mega-menu-open', String(isOpen))

  if (isOpen) {
    lockPageScroll()
  } else {
    unlockPageScroll()
  }
}

/**
 * @param {string | undefined} tab
 * @returns {tab is 'products' | 'help'}
 */
export function isValidMegaMenuMobileTab(tab) {
  return tab === 'products' || tab === 'help'
}

/**
 * @param {Iterable<Element>} buttons
 * @param {'products' | 'help'} tab
 */
export function syncMegaMenuMobileTabs(buttons, tab) {
  for (const btn of buttons) {
    if (!(btn instanceof HTMLElement)) continue
    const btnTab = btn.dataset.megaMenuMobileTab
    btn.setAttribute('aria-selected', btnTab === tab ? 'true' : 'false')
  }
}

/**
 * @param {{ isOpen: boolean, targetIsNode: boolean, containsTarget: boolean, triggerContainsTarget: boolean }} params
 * @returns {boolean}
 */
export function shouldCloseMegaMenuFromDocumentClick({
  isOpen,
  targetIsNode,
  containsTarget,
  triggerContainsTarget
}) {
  if (!isOpen || !targetIsNode) return false
  return !containsTarget && !triggerContainsTarget
}

/**
 * Mirrors aria-hidden during mobile panel close animation.
 *
 * @param {HTMLElement} panel
 */
export function beginMobilePanelCloseMirror(panel) {
  panel.setAttribute('aria-hidden', 'true')
  delete panel.dataset.megaMenuPanelOpen
}

/**
 * @param {{ isExpanded: boolean, item: HTMLElement | null, isTrusted: boolean }} params
 * @returns {boolean}
 */
export function shouldSkipTrustedHoverToggle({ isExpanded, item, isTrusted }) {
  return Boolean(
    isTrusted &&
    isExpanded &&
    item instanceof HTMLElement &&
    item.dataset.hoverOpened === 'true'
  )
}

/** Mega menu: `<header-mega-menu>`; listeners abort on disconnect. */
class HeaderMegaMenuElement extends HTMLElement {
  /** @type {AbortController | null} */
  #lifetime = null

  /** @type {AbortController | null} */
  #whileOpen = null

  /** @type {HTMLElement | null} */
  #header = null

  /** @type {HTMLElement | null} */
  #headerGroup = null

  /** @type {HTMLButtonElement | null} */
  #trigger = null

  /** @type {HTMLDivElement | null} */
  #layout = null

  /** @type {string} */
  #openLabel = ''

  /** @type {string} */
  #closeLabel = ''

  /** @type {HTMLElement | null} */
  #opener = null

  /** @type {WeakMap<HTMLElement, () => void>} */
  #pendingTransitionCleanups = new WeakMap()

  /** @type {Map<HTMLElement, number> | null} */
  #hoverTimers = null

  /** @type {Map<HTMLElement, number> | null} */
  #enterTimers = null

  #isMobile = () =>
    window.matchMedia &&
    window.matchMedia(`(max-width: ${THEME_BREAKPOINTS.megaMenuMobileMax}px)`)
      .matches

  /**
   * @param {boolean} isOpen
   */
  #setMenuOpenState(isOpen) {
    setMegaMenuOpenState({ headerGroup: this.#headerGroup, isOpen })
  }

  /**
   * @param {HTMLElement} element
   */
  #clearPendingTransition(element) {
    const cleanup = this.#pendingTransitionCleanups.get(element)
    if (!cleanup) return

    cleanup()
    this.#pendingTransitionCleanups.delete(element)
  }

  /**
   * @param {HTMLElement} element
   * @param {string} propertyName
   * @param {() => void} callback
   */
  #waitForTransition(element, propertyName, callback) {
    this.#clearPendingTransition(element)

    const timeoutMs = getTransitionTimeoutMs(element, propertyName)
    const prefersReducedMotion =
      window.matchMedia?.('(prefers-reduced-motion: reduce)').matches ?? false

    if (prefersReducedMotion || timeoutMs === 0) {
      callback()
      return
    }

    let isSettled = false
    const finish = () => {
      if (isSettled) return
      isSettled = true
      window.clearTimeout(timeoutId)
      element.removeEventListener('transitionend', handleTransitionEnd)
      this.#pendingTransitionCleanups.delete(element)
      callback()
    }

    /** @param {TransitionEvent} event */
    const handleTransitionEnd = (event) => {
      if (event.target !== element) return
      if (event.propertyName !== propertyName) return
      finish()
    }

    const timeoutId = window.setTimeout(finish, timeoutMs + 50)
    element.addEventListener('transitionend', handleTransitionEnd)
    this.#pendingTransitionCleanups.set(element, () => {
      window.clearTimeout(timeoutId)
      element.removeEventListener('transitionend', handleTransitionEnd)
    })
  }

  /** @param {HTMLButtonElement} toggle */
  #getSupportPanel(toggle) {
    return resolvePanelByControls(this, toggle)
  }

  /** @param {HTMLButtonElement} toggle */
  #collapseSupportPanel(toggle) {
    toggle.setAttribute('aria-expanded', 'false')
    syncSupportItemExpandedState(toggle)
    const panel = this.#getSupportPanel(toggle)
    if (panel) {
      if (panel.dataset.megaMenuPanelOpen === 'true') {
        delete panel.dataset.megaMenuPanelOpen
      }
      panel.hidden = true
      panel.removeAttribute('aria-hidden')
    }
  }

  #resetSupportPanels() {
    this.#supportToggles.forEach((toggle) => this.#collapseSupportPanel(toggle))
  }

  /** @type {NodeListOf<HTMLButtonElement>} */
  get #mobileTabButtons() {
    return this.querySelectorAll('[data-mega-menu-mobile-tab]')
  }

  /** @type {HTMLButtonElement | null} */
  get #mobileBackButton() {
    const el = this.querySelector('[data-mega-menu-mobile-back]')
    return el instanceof HTMLButtonElement ? el : null
  }

  /** @type {HTMLElement | null} */
  get #mobileTitle() {
    const el = this.querySelector('[data-mega-menu-mobile-title]')
    return el instanceof HTMLElement ? el : null
  }

  /** @type {HTMLElement | null} */
  get #mobileIcon() {
    return this.querySelector('[data-mega-menu-mobile-icon]')
  }

  /** @type {NodeListOf<HTMLButtonElement>} */
  get #primaryToggles() {
    return this.querySelectorAll('[data-mega-menu-primary-toggle]')
  }

  /** @type {NodeListOf<HTMLElement>} */
  get #primaryPanels() {
    return this.querySelectorAll('[data-mega-menu-primary-panel]')
  }

  /** @type {NodeListOf<HTMLButtonElement>} */
  get #supportToggles() {
    return this.querySelectorAll('[data-mega-menu-support-toggle]')
  }

  /**
   * @param {HTMLElement} panel
   * @param {boolean} openPanel
   */
  #setMobilePanelOpen(panel, openPanel) {
    this.#clearPendingTransition(panel)

    if (!this.#isMobile()) {
      panel.hidden = !openPanel
      panel.removeAttribute('aria-hidden')
      return
    }

    if (openPanel) {
      panel.hidden = false
      panel.removeAttribute('aria-hidden')
      requestAnimationFrame(() => {
        panel.dataset.megaMenuPanelOpen = 'true'
      })
      return
    }

    beginMobilePanelCloseMirror(panel)

    this.#waitForTransition(panel, 'transform', () => {
      panel.hidden = true
      panel.removeAttribute('aria-hidden')
    })
  }

  /**
   * @param {'products' | 'help'} tab
   */
  #setMobileTab(tab) {
    if (!(this.#layout instanceof HTMLElement)) return
    this.#layout.dataset.megaMenuTab = tab

    syncMegaMenuMobileTabs(this.#mobileTabButtons, tab)
  }

  #resetMobileSubpage() {
    if (!(this.#layout instanceof HTMLElement)) return
    this.#layout.dataset.megaMenuSubpage = ''
    this.#layout.dataset.megaMenuActiveLabel = ''
    this.#layout.style.removeProperty('--mega-menu-active-bg')
    const mobileTitle = this.#mobileTitle
    if (mobileTitle) mobileTitle.textContent = ''
    const mobileIcon = this.#mobileIcon
    if (mobileIcon) {
      mobileIcon.replaceChildren()
      mobileIcon.hidden = true
    }
  }

  #resetPrimaryPanels(instant = false) {
    this.#primaryToggles.forEach((toggle) => {
      toggle.setAttribute('aria-expanded', 'false')
    })

    this.#primaryPanels.forEach((panel) => {
      if (panel.dataset.megaMenuPanelOpen === 'true') {
        if (instant) {
          beginMobilePanelCloseMirror(panel)
          panel.hidden = true
          panel.removeAttribute('aria-hidden')
          delete panel.dataset.megaMenuPanelOpen
        } else {
          this.#setMobilePanelOpen(panel, false)
        }
      } else {
        panel.hidden = true
        panel.removeAttribute('aria-hidden')
      }
      delete panel.dataset.megaMenuPanelOpen
      this.#hideAllSubSubpanels(panel)
    })

    this.querySelectorAll('[data-mega-menu-primary-item]').forEach((item) => {
      item.classList.remove('header-mega-menu__primary-item--active')
      if (item instanceof HTMLElement) {
        delete item.dataset.hoverOpened
      }
    })
  }

  #showSubSubpanel(item) {
    const parentPanel = item.closest('[data-mega-menu-primary-panel]')
    if (!parentPanel) return

    parentPanel
      .querySelectorAll('[data-mega-menu-sublevel-item]')
      .forEach((otherItem) => {
        if (otherItem !== item) {
          otherItem.classList.remove('header-mega-menu__sublevel-item--active')
          const toggle = otherItem.querySelector(
            '[data-mega-menu-sublevel-toggle]'
          )
          toggle?.setAttribute('aria-expanded', 'false')
          const panelId = toggle?.getAttribute('aria-controls')
          const subsub = panelId ? this.querySelector(`#${panelId}`) : null
          if (subsub) {
            subsub.hidden = true
            subsub.removeAttribute('aria-hidden')
          }
        }
      })

    item.classList.add('header-mega-menu__sublevel-item--active')
    const toggle = item.querySelector('[data-mega-menu-sublevel-toggle]')
    toggle?.setAttribute('aria-expanded', 'true')
    const panelId = toggle?.getAttribute('aria-controls')
    const subsub = panelId ? this.querySelector(`#${panelId}`) : null
    if (subsub) {
      subsub.hidden = false
      subsub.removeAttribute('aria-hidden')
    }
  }

  #hideAllSubSubpanels(parentPanel) {
    parentPanel
      .querySelectorAll('[data-mega-menu-sublevel-item]')
      .forEach((item) => {
        item.classList.remove('header-mega-menu__sublevel-item--active')
        const toggle = item.querySelector('[data-mega-menu-sublevel-toggle]')
        toggle?.setAttribute('aria-expanded', 'false')
        const panelId = toggle?.getAttribute('aria-controls')
        const subsub = panelId ? this.querySelector(`#${panelId}`) : null
        if (subsub) {
          subsub.hidden = true
          subsub.removeAttribute('aria-hidden')
        }
      })
  }

  /**
   * @param {'default' | 'sublevel'} state
   */
  #setState(state) {
    if (!(this.#layout instanceof HTMLElement)) return
    this.#layout.dataset.megaMenuState = state
  }

  #finalizeCloseMenu = () => {
    this.#setMenuOpenState(false)
    this.hidden = true
    this.#clearPendingTransition(this)
    removeTrapFocus()

    const target = this.#opener ?? this.#trigger
    target?.focus({ preventScroll: true })
    this.#opener = null
  }

  /**
   * @param {MouseEvent} event
   */
  #handleDocumentClickCloseMenu = (event) => {
    const trigger = this.#trigger
    if (!trigger) return
    const isOpen = trigger.getAttribute('aria-expanded') === 'true'
    if (!isOpen) return

    const target = event.target
    if (!(target instanceof Node)) return

    const isInsideDialog = this.contains(target)
    const isOnTrigger = trigger.contains(target)

    if (
      shouldCloseMegaMenuFromDocumentClick({
        isOpen: true,
        targetIsNode: true,
        containsTarget: isInsideDialog,
        triggerContainsTarget: isOnTrigger
      })
    ) {
      this.#closeMenu()
    }
  }

  /**
   * @param {KeyboardEvent} event
   */
  #handleDocumentKeydownEscape = (event) => {
    if (event.key !== 'Escape') return
    const trigger = this.#trigger
    if (!trigger) return
    const isOpen = trigger.getAttribute('aria-expanded') === 'true'
    if (!isOpen) return

    event.preventDefault()
    event.stopPropagation()
    this.#closeMenu()
  }

  #abortWhileOpenListeners() {
    this.#whileOpen?.abort()
    this.#whileOpen = null
  }

  /**
   * @param {HTMLElement} openerElement
   */
  #open(openerElement) {
    const trigger = this.#trigger
    if (!trigger) return

    this.#clearPendingTransition(this)
    this.#opener = openerElement
    trigger.setAttribute('aria-expanded', 'true')
    if (this.#closeLabel) {
      trigger.setAttribute('aria-label', this.#closeLabel)
    }

    this.#setMenuOpenState(true)
    this.hidden = false
    this.classList.add('header-mega-menu--open')

    if (!this.hasAttribute('data-editor-preview')) {
      trapFocus(this)
    }

    this.#abortWhileOpenListeners()
    this.#whileOpen = new AbortController()
    const openSignal = this.#whileOpen.signal

    document.addEventListener('click', this.#handleDocumentClickCloseMenu, {
      signal: openSignal
    })
    document.addEventListener('keydown', this.#handleDocumentKeydownEscape, {
      capture: true,
      signal: openSignal
    })
  }

  #closeMenu = () => {
    const trigger = this.#trigger
    if (!trigger) return

    this.#abortWhileOpenListeners()
    trigger.setAttribute('aria-expanded', 'false')
    if (this.#openLabel) {
      trigger.setAttribute('aria-label', this.#openLabel)
    }
    this.classList.remove('header-mega-menu--open')
    this.#setState('default')
    this.#resetMobileSubpage()
    this.#resetSupportPanels()
    this.#resetPrimaryPanels(true)

    this.#waitForTransition(this, 'opacity', this.#finalizeCloseMenu)
  }

  #clearHoverTimers() {
    this.#hoverTimers?.forEach((id) => clearTimeout(id))
    this.#hoverTimers?.clear()
    this.#enterTimers?.forEach((id) => clearTimeout(id))
    this.#enterTimers?.clear()
  }

  connectedCallback() {
    this.#header = document.querySelector('#header-component')
    if (!this.#header) return

    this.#headerGroup = document.querySelector('#header-group')

    const trigger =
      this.#header.querySelector('[data-mega-menu-trigger]') ??
      this.#header.querySelector(
        '.header__icon--menu[aria-controls^="HeaderMegaMenu-"]'
      )

    if (!(trigger instanceof HTMLButtonElement)) return

    this.#trigger = trigger
    this.#layout = this.querySelector('.header-mega-menu__layout')

    this.#openLabel =
      trigger.dataset.openLabel || trigger.getAttribute('aria-label') || ''
    this.#closeLabel = trigger.dataset.closeLabel || this.#openLabel

    this.#lifetime = new AbortController()
    const signal = this.#lifetime.signal

    trigger.addEventListener(
      'click',
      /** @param {MouseEvent} event */ (event) => {
        event.preventDefault()
        const expanded = trigger.getAttribute('aria-expanded') === 'true'
        if (expanded) {
          this.#closeMenu()
        } else {
          if (this.#isMobile()) this.#setMobileTab('products')
          this.#open(trigger)
        }
      },
      { signal }
    )

    const mobileTabButtons = this.#mobileTabButtons
    if (mobileTabButtons.length) {
      mobileTabButtons.forEach((btn) => {
        btn.addEventListener(
          'click',
          () => {
            const tab = btn.dataset.megaMenuMobileTab
            if (!isValidMegaMenuMobileTab(tab)) return
            this.#resetPrimaryPanels()
            this.#resetSupportPanels()
            this.#resetMobileSubpage()
            this.#setState('default')
            this.#setMobileTab(tab)
          },
          { signal }
        )
      })
    }

    const mobileBackButton = this.#mobileBackButton
    if (mobileBackButton) {
      mobileBackButton.addEventListener(
        'click',
        () => {
          this.#resetPrimaryPanels()
          this.#resetSupportPanels()
          this.#resetMobileSubpage()
          this.#setState('default')
        },
        { signal }
      )
    }

    const primaryToggles = this.#primaryToggles
    const primaryPanels = this.#primaryPanels

    if (primaryToggles.length && primaryPanels.length) {
      primaryToggles.forEach((toggle) => {
        const panelId = toggle.getAttribute('aria-controls')
        const panelNode = panelId ? this.querySelector(`#${panelId}`) : null
        const panel = panelNode instanceof HTMLElement ? panelNode : null

        toggle.addEventListener(
          'click',
          (event) => {
            const isExpanded = toggle.getAttribute('aria-expanded') === 'true'
            const item = toggle.closest('[data-mega-menu-primary-item]')

            if (
              shouldSkipTrustedHoverToggle({
                isExpanded,
                item: item instanceof HTMLElement ? item : null,
                isTrusted: event.isTrusted
              })
            ) {
              if (item instanceof HTMLElement) {
                delete item.dataset.hoverOpened
              }
              return
            }

            primaryToggles.forEach((otherToggle) => {
              otherToggle.setAttribute('aria-expanded', 'false')
            })

            primaryPanels.forEach((otherPanel) => {
              otherPanel.hidden = true
              otherPanel.removeAttribute('aria-hidden')
              delete otherPanel.dataset.megaMenuPanelOpen
            })

            this.querySelectorAll('[data-mega-menu-primary-item]').forEach(
              (otherItem) => {
                otherItem.classList.remove(
                  'header-mega-menu__primary-item--active'
                )
                if (otherItem instanceof HTMLElement) {
                  delete otherItem.dataset.hoverOpened
                }
              }
            )

            if (isExpanded) {
              this.#resetMobileSubpage()
              this.#setState('default')
              return
            }

            toggle.setAttribute('aria-expanded', 'true')
            if (item instanceof HTMLElement && !event.isTrusted) {
              item.dataset.hoverOpened = 'true'
            }

            if (panel) {
              this.#setMobilePanelOpen(panel, true)
            }

            const layout = this.#layout
            const mobileTitle = this.#mobileTitle
            if (item instanceof HTMLElement) {
              item.classList.add('header-mega-menu__primary-item--active')
              if (layout instanceof HTMLElement && this.#isMobile()) {
                layout.dataset.megaMenuSubpage = 'products'
                const titleEl = item.querySelector(
                  '.header-mega-menu__primary-title'
                )
                if (titleEl) {
                  layout.dataset.megaMenuActiveLabel = titleEl.textContent || ''
                  if (mobileTitle)
                    mobileTitle.textContent = layout.dataset.megaMenuActiveLabel
                }

                const bgImg = item.querySelector(
                  '.header-mega-menu__primary-bg'
                )
                if (bgImg instanceof HTMLImageElement) {
                  const candidateSrc = bgImg.currentSrc || bgImg.src
                  const isLoaded = bgImg.complete && bgImg.naturalWidth > 0
                  const isUsableUrl =
                    candidateSrc && !candidateSrc.startsWith('data:')

                  if (isLoaded && isUsableUrl) {
                    layout.style.setProperty(
                      '--mega-menu-active-bg',
                      `url("${candidateSrc}")`
                    )
                  } else {
                    layout.style.removeProperty('--mega-menu-active-bg')
                  }
                } else {
                  layout.style.removeProperty('--mega-menu-active-bg')
                }
              }
            }

            this.#setState('sublevel')
          },
          { signal }
        )
      })
    }

    const primaryList = this.querySelector('.header-mega-menu__primary-list')
    const hoverEnabled =
      primaryList instanceof HTMLElement &&
      primaryList.dataset.megaMenuHoverEnabled === 'true'

    if (hoverEnabled && primaryToggles.length && primaryPanels.length) {
      this.#hoverTimers = new Map()
      this.#enterTimers = new Map()
      const hoverTimers = this.#hoverTimers
      const enterTimers = this.#enterTimers

      const primaryItems = this.querySelectorAll(
        '[data-mega-menu-primary-item]'
      )

      primaryItems.forEach((item) => {
        if (!(item instanceof HTMLElement)) return
        const toggle = item.querySelector('[data-mega-menu-primary-toggle]')
        if (!(toggle instanceof HTMLButtonElement)) return

        item.addEventListener(
          'mouseenter',
          () => {
            if (this.#isMobile()) return

            const leaveTimer = hoverTimers.get(item)
            if (leaveTimer) {
              clearTimeout(leaveTimer)
              hoverTimers.delete(item)
            }

            const pendingEnter = enterTimers.get(item)
            if (pendingEnter) {
              clearTimeout(pendingEnter)
              enterTimers.delete(item)
            }

            const isExpanded = toggle.getAttribute('aria-expanded') === 'true'
            if (!isExpanded) {
              const isAnotherPanelOpen = Array.from(primaryToggles).some(
                (t) =>
                  t !== toggle && t.getAttribute('aria-expanded') === 'true'
              )

              if (isAnotherPanelOpen) {
                const newEnterTimer = window.setTimeout(() => {
                  enterTimers.delete(item)
                  if (toggle.getAttribute('aria-expanded') !== 'true') {
                    toggle.click()
                  }
                }, 50)
                enterTimers.set(item, newEnterTimer)
              } else {
                toggle.click()
              }
            }
          },
          { signal }
        )

        item.addEventListener(
          'mouseleave',
          () => {
            if (this.#isMobile()) return

            const pendingEnter = enterTimers.get(item)
            if (pendingEnter) {
              clearTimeout(pendingEnter)
              enterTimers.delete(item)
            }

            const isExpanded = toggle.getAttribute('aria-expanded') === 'true'
            if (!isExpanded) return

            if (item.dataset.hoverOpened !== 'true') return

            const timer = window.setTimeout(() => {
              hoverTimers.delete(item)
              if (toggle.getAttribute('aria-expanded') === 'true') {
                toggle.click()
              }
            }, 500)
            hoverTimers.set(item, timer)
          },
          { signal }
        )
      })
    }

    const supportToggles = this.#supportToggles
    if (supportToggles.length) {
      supportToggles.forEach((toggle) => {
        const panel = this.#getSupportPanel(toggle)

        toggle.addEventListener(
          'click',
          () => {
            if (this.#isMobile() && panel) {
              const expanded = toggle.getAttribute('aria-expanded') === 'true'

              supportToggles.forEach((otherToggle) => {
                if (otherToggle === toggle) return
                const otherPanel = this.#getSupportPanel(otherToggle)
                otherToggle.setAttribute('aria-expanded', 'false')
                if (otherPanel) {
                  otherPanel.hidden = true
                  otherPanel.removeAttribute('aria-hidden')
                  delete otherPanel.dataset.megaMenuPanelOpen
                }
              })

              if (expanded) {
                toggle.setAttribute('aria-expanded', 'false')
                this.#setMobilePanelOpen(panel, false)
                this.#resetMobileSubpage()
                this.#setState('default')
                return
              }

              toggle.setAttribute('aria-expanded', 'true')
              this.#setMobilePanelOpen(panel, true)
              this.#setMobileTab('help')
              this.#setState('sublevel')

              const layout = this.#layout
              const mobileTitle = this.#mobileTitle
              const mobileIcon = this.#mobileIcon
              if (layout instanceof HTMLElement) {
                layout.dataset.megaMenuSubpage = 'help'
                const label =
                  toggle.querySelector('.header-mega-menu__support-label')
                    ?.textContent || ''
                layout.dataset.megaMenuActiveLabel = label
                if (mobileTitle) mobileTitle.textContent = label
                if (mobileIcon) {
                  const toggleIcon = toggle.querySelector(
                    '.header-mega-menu__support-menu-icon'
                  )
                  if (toggleIcon) {
                    mobileIcon.replaceChildren(toggleIcon.cloneNode(true))
                    mobileIcon.hidden = false
                  } else {
                    mobileIcon.replaceChildren()
                    mobileIcon.hidden = true
                  }
                }
                layout.style.removeProperty('--mega-menu-active-bg')
              }
              return
            }

            const expanded = toggle.getAttribute('aria-expanded') === 'true'
            supportToggles.forEach((otherToggle) => {
              if (otherToggle !== toggle) {
                this.#collapseSupportPanel(otherToggle)
              }
            })

            toggle.setAttribute('aria-expanded', expanded ? 'false' : 'true')
            syncSupportItemExpandedState(toggle)
            if (panel) {
              panel.hidden = expanded
              panel.removeAttribute('aria-hidden')
            }
          },
          { signal }
        )
      })
    }

    const sublevelItems = this.querySelectorAll(
      '[data-mega-menu-sublevel-item]'
    )
    sublevelItems.forEach((item) => {
      if (!(item instanceof HTMLElement)) return

      item.addEventListener(
        'mouseenter',
        () => {
          if (this.#isMobile()) return
          if (
            item.classList.contains(
              'header-mega-menu__sublevel-item--has-children'
            )
          ) {
            this.#showSubSubpanel(item)
          } else {
            const parentPanel = item.closest('[data-mega-menu-primary-panel]')
            if (parentPanel) {
              this.#hideAllSubSubpanels(parentPanel)
            }
          }
        },
        { signal }
      )
    })

    const sublevelToggles = this.querySelectorAll(
      '[data-mega-menu-sublevel-toggle]'
    )
    sublevelToggles.forEach((toggle) => {
      if (!(toggle instanceof HTMLButtonElement)) return

      toggle.addEventListener(
        'click',
        (event) => {
          const item = toggle.closest('[data-mega-menu-sublevel-item]')
          if (!(item instanceof HTMLElement)) return

          if (this.#isMobile()) {
            event.preventDefault()
            const panelId = toggle.getAttribute('aria-controls')
            const subsubpanel = panelId
              ? this.querySelector(`#${panelId}`)
              : null
            const isExpanded = toggle.getAttribute('aria-expanded') === 'true'

            if (isExpanded) {
              toggle.setAttribute('aria-expanded', 'false')
              if (subsubpanel instanceof HTMLElement) {
                subsubpanel.hidden = true
                subsubpanel.removeAttribute('aria-hidden')
              }
              item.classList.remove('header-mega-menu__sublevel-item--active')
            } else {
              const parentPanel = item.closest('[data-mega-menu-primary-panel]')
              if (parentPanel instanceof HTMLElement) {
                parentPanel
                  .querySelectorAll('[data-mega-menu-sublevel-toggle]')
                  .forEach((t) => {
                    t.setAttribute('aria-expanded', 'false')
                  })
                parentPanel
                  .querySelectorAll('[data-mega-menu-sub-subpanel]')
                  .forEach((p) => {
                    p.hidden = true
                    p.removeAttribute('aria-hidden')
                  })
                parentPanel
                  .querySelectorAll('[data-mega-menu-sublevel-item]')
                  .forEach((i) => {
                    i.classList.remove(
                      'header-mega-menu__sublevel-item--active'
                    )
                  })
              }

              toggle.setAttribute('aria-expanded', 'true')
              if (subsubpanel instanceof HTMLElement) {
                subsubpanel.hidden = false
                subsubpanel.removeAttribute('aria-hidden')
              }
              item.classList.add('header-mega-menu__sublevel-item--active')
            }
          } else {
            this.#showSubSubpanel(item)
          }
        },
        { signal }
      )
    })

    this.addEventListener(
      'click',
      (event) => {
        const target = event.target
        if (!(target instanceof Node)) return

        const expandedToggle = Array.from(this.#primaryToggles).find(
          (t) => t.getAttribute('aria-expanded') === 'true'
        )

        if (!expandedToggle) return

        const panelId = expandedToggle.getAttribute('aria-controls')
        const panel = panelId ? this.querySelector(`#${panelId}`) : null

        const isClickOnToggle = expandedToggle.contains(target)
        const isClickInsidePanel =
          panel instanceof HTMLElement && panel.contains(target)

        if (!isClickOnToggle && !isClickInsidePanel) {
          this.#resetPrimaryPanels()
          this.#resetMobileSubpage()
          this.#setState('default')
        }
      },
      { signal }
    )
  }

  disconnectedCallback() {
    if (this.#trigger?.getAttribute('aria-expanded') === 'true') {
      this.#setMenuOpenState(false)
    }

    this.#abortWhileOpenListeners()
    this.#lifetime?.abort()
    this.#lifetime = null
    this.#clearPendingTransition(this)
    this.#primaryPanels.forEach((panel) => this.#clearPendingTransition(panel))
    removeTrapFocus()
    this.#clearHoverTimers()
    this.#hoverTimers = null
    this.#enterTimers = null
    this.#trigger = null
    this.#header = null
    this.#headerGroup = null
    this.#layout = null
    this.#opener = null
  }
}

if (!customElements.get('header-mega-menu')) {
  customElements.define('header-mega-menu', HeaderMegaMenuElement)
}
